import KeyMirror from 'keymirror'
import { LEVEL_TYPES } from '../../../../constants'

export const BOBI_REPORT_LIST_CONTEXT_KEY = 'BOBI_REPORT_LIST_CONTEXT_KEY'

export const EXPOSURE_TYPES = {
  ASSET: 'asset',
  CLASSIFICATION_TAG: 'classificationTag'
}

export const DATA_SET_TYPES = {
  BALANCE: 'balance',
  PERFORMANCE: 'performance',
  ALL: ['balance', 'performance'],
  BALANCE_AND_PERFORMANCE: 'balanceAndPerformance'
}

export const levelDatesQuery = { levelType: 'user' }

export const REPORT_STATUS = {
  ERROR: 'error',
  COMPLETED: 'completed',
  PENDING: 'pending'
}
/**
 *
 * @param {string} string - titleCaseWRD
 * @returns string - TitleCaseWRD
 */
export const toTitleCase = (string) => {
  return string
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .split('/s')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
}

/**
 *
 * @param {string} prefixKey - key separator (.)
 * @param {object} dataPoints - { balance: { endingValue: 2 }}
 * @returns { 'balance.endingValue': { titleCase: 'Ending Value', template: 'money' } }
 */
export const mapDataPointsToTitleCase = ({ prefixKey = null, dataPoints }) => {
  return Object.fromEntries(
    Object.entries(dataPoints).reduce((acc, [key, value]) => {
      const titleCase = toTitleCase(key)
      if (!!value && value.constructor === Object) {
        const valueDataPoints = mapDataPointsToTitleCase({
          prefixKey: key,
          dataPoints: value
        })
        return [...acc, ...Object.entries(valueDataPoints)]
      }
      return [
        ...acc,
        [
          `${prefixKey ? `${prefixKey}.` : ''}${key}`,
          { titleCase, template: 'money' }
        ]
      ]
    }, [])
  )
}

export const DATA_SETS = KeyMirror({
  balance: null,
  performance: null,
  ugl: null,
  rgl: null
})

export const defaultDataSetOptions = [
  {
    label: 'Balance',
    value: 'balance',
    footnote: 'Market Value and Units'
  },
  {
    label: 'Performance',
    value: 'performance',
    footnote: 'Incl. Net Gain and Return'
  },
  {
    label: 'UGL',
    value: 'ugl',
    footnote: 'Unrealized Gain/Loss'
  },
  {
    label: 'RGL',
    value: 'rgl',
    footnote: 'Realized Gain/Loss'
  }
]

export const getGroupTypesQuery = (levelType, groupTypeId) => {
  return {
    query: {
      includes: {
        groups: true,
        groupMembers: true
      },
      filters: {
        ...(groupTypeId
          ? { groupTypeId: [{ op: 'in', value: [groupTypeId] }] }
          : {}),
        memberLevelTypeIds:
          levelType === LEVEL_TYPES.ACCOUNTS ? [1] : [201, 1]
      }
    },
    queryOptions: {
      mapper: ({ data }) =>
        data?.reduce(
          (acc, groupType) => [
            ...acc,
            ...(groupType?.groups ?? []).map((group) => ({
              label: group.shortName,
              value: group.groupId
            }))
          ],
          []
        ) ?? []
    }
  }
}

export const mapGroupTypeFilters = ({ data }) => {
  return data
    .map(({ shortName, longName, memberTypes, groupTypeId }) => ({
      label: longName,
      value: shortName,
      payload: { groupTypeId, ...(memberTypes?.[0] || {}) }
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
    .concat([
      {
        value: EXPOSURE_TYPES.ASSET,
        label: 'Asset'
      },
      {
        value: EXPOSURE_TYPES.CLASSIFICATION_TAG,
        label: 'Asset Tag'
      }
    ])
}

export const getGroupTypeFiltersQuery = (levelType) => {
  return {
    includes: {
      groups: true,
      groupMembers: true,
      groupMemberTypes: true
    },
    filters: {
      memberLevelTypeIds: levelType === LEVEL_TYPES.ACCOUNTS ? [1] : [201, 1]
    }
  }
}

export const FILTER_TYPES = KeyMirror({
  [EXPOSURE_TYPES.ASSET]: null,
  [EXPOSURE_TYPES.CLASSIFICATION_TAG]: null,
  groupTypes: null
})

export const FILTER_LABELS = {
  [EXPOSURE_TYPES.ASSET]: 'Asset',
  [EXPOSURE_TYPES.CLASSIFICATION_TAG]: 'Asset Tag',
  groupTypes: null
}

export const getReportFilters = (reportParams) => {
  return (reportParams?.filters || []).map((filter) => {
    const { type, value } = filter
    const isExposure = Object.values(EXPOSURE_TYPES).includes(
      reportParams?.exposureType
    )

    if (isExposure) {
      const values = value.map((val) => val?.value || val)
      return {
        label: FILTER_LABELS[type.replace('Ids', '')],
        defaultValues: values,
        type
      }
    }
    return {
      label: type,
      defaultValues: value,
      type: FILTER_TYPES.groupTypes
    }
  })
}

export const escapeString = function (val) {
  // eslint-disable-next-line no-control-regex
  val = val.replace(/[\0\n\r\b\t\\'"\x1a]/g, function (s) {
    switch (s) {
      case '\0':
        return '\\0'
      case '\n':
        return '\\n'
      case '\r':
        return '\\r'
      case '\b':
        return '\\b'
      case '\t':
        return '\\t'
      case '\x1a':
        return '\\Z'
      case "'":
        return "''"
      case '"':
        return '""'
      default:
        return '\\' + s
    }
  })
  return val
}

export const getAdditionalData = ({ levelType }) => {
  if (levelType === 'account') {
    return {
      accounts: [
        'startDate',
        'performanceStartDate',
        'dailyStartDate',
        'managedStatusId',
        'discretionaryStatusId',
        'performanceStatusId',
        'taxStatusId',
        'custodianId',
        'accountRegistration'
      ],
      custodians: ['name'],
      dataSources: ['name'],
      taxStatus: ['longName'],
      managedStatus: ['name'],
      discretionaryStatus: ['name'],
      performanceStatus: ['name']
    }
  }
  return {}
}
